import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Button,
    Autocomplete,
    TextField,
    useTheme,
    useMediaQuery,
    Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';

// IMPORT THE NEW CAROUSEL
import Interactive3DCarousel from './Interactive3DCarousel';

import heroBg from '../assets/image/Background Banner-03.png';

// ✅ Import encoder
import { encodeId } from '../utils/idEncoder';

// --- 🎨 STYLED COMPONENTS ---

const HeroWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundImage: `url("${heroBg}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    paddingBottom: '20px',
    [theme.breakpoints.down('sm')]: {
        minHeight: 'auto',
        paddingTop: '80px',
        paddingBottom: '30px',
    }
}));

const HeroSection = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Data State
    const [adSpaceList, setAdSpaceList] = useState<any[]>([]);
    const [vehicleBrandData, setVehicleBrandData] = useState<any[]>([]);
    const [vehicleModelData, setVehicleModelData] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');

    // --- Fetching Logic (Ads & Search Data Only) ---
    useEffect(() => {
        const fetchAdData = async () => {
            try {
                const response = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList", { adSpaceData: true }, { headers: { "Content-Type": "application/json" } });
                const result = response.data as any;
                if (result.responseCode === "00" && result.responseData) setAdSpaceList(result.responseData.adSpaceData || []);
            } catch { console.error("Error fetching ads"); }
        };
        fetchAdData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axiosInstance.post("/CustomerVehicle/VehicleMultipleDataList",
                    { vehicleBrandData: true, vehicleModelData: true },
                    { headers: { "Content-Type": "application/json" } }
                );
                const result = response.data as any;
                if (result.responseCode === "00" && result.responseData) {
                    setVehicleBrandData(result.responseData.vehicleBrandData || []);
                    setVehicleModelData(result.responseData.vehicleModelData || []);
                }
            } catch { console.error("Error fetching search data"); }
        };
        fetchData();
    }, []);

    // Search Helpers
    const combinedOptions = [
        // ✅ Include ID for encoding
        ...vehicleBrandData.map((b) => ({ type: 'Brand', label: b.description, id: b.vehicleBrandId })),
        ...vehicleModelData.map((m) => ({ type: 'Model', label: m.description, id: m.vehicleModelId })),
    ];

    const handleSearch = (query: any) => {
        // 1. Handle Free Text (Enter key or typing)
        if (typeof query === 'string') {
            if (query.trim().length > 0) {
                navigate(`/vehicle-listing?searchKeyword=${encodeURIComponent(query.trim())}`);
            }
            return;
        }

        if (!query) return;

        // 2. Handle Selected Option Object
        if (query.type === 'Brand' && query.id) {
            // ✅ ENCODE: Use encoded Brand ID filter
            navigate(`/vehicle-listing?vehicleBrandIds=${encodeId(query.id)}`);
        } else if (query.type === 'Model') {
            // Model specific search (usually text based in this app logic)
            navigate(`/vehicle-listing?vehicleModel=${encodeURIComponent(query.label)}`);
        } else {
            // Fallback to generic keyword search
            const term = query.label || searchText;
            if (term && term.trim().length > 0) {
                navigate(`/vehicle-listing?searchKeyword=${encodeURIComponent(term.trim())}`);
            }
        }
    };

    return (
        <HeroWrapper>
            <Grid container spacing={1} sx={{ px: { xs: 2, md: 4 }, justifyContent: 'center', position: 'relative', zIndex: 1, flexGrow: 1 }}>

                {/* --- 1. HEADLINE & SUBTITLE --- */}
                <Grid size={{ xs: 12, md: 12 }} sx={{ textAlign: 'center', mt: { xs: 2, md: 4 } }}>
                    <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h3"
                        sx={{
                            fontWeight: 600,
                            color: '#1A2027',
                            lineHeight: { xs: 1.2, md: 1.3 },
                            fontFamily: 'Inter, sans-serif',
                            textShadow: '0px 4px 20px rgba(255,255,255,0.8)',
                            maxWidth: '850px',
                            mx: 'auto',
                        }}
                    >
                        Get matched with the right <br />Commercial Vehicle <br />backed by real market data.
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: '600px',
                            mx: 'auto',
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            fontWeight: 500,
                            fontFamily: 'Inter, sans-serif',
                            mt: 2,
                            mb: 2
                        }}
                    >
                        Compare prices, COE trends, and demand.<br />Connect with verified sellers faster.
                    </Typography>
                </Grid>

                {/* --- 2. SEARCH BAR --- */}
                <Grid size={{ xs: 12, md: 8 }} sx={{ mx: 'auto', mb: 1 }}>
                    <Paper
                        elevation={4}
                        sx={{
                            p: '4px',
                            pl: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: '50px',
                            width: '100%',
                            maxWidth: '750px',
                            mx: 'auto',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.8)',
                        }}
                    >
                        <Autocomplete
                            fullWidth
                            freeSolo
                            size="small"
                            options={searchText.length > 0 ? combinedOptions : []}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                            onChange={(event, value) => handleSearch(value)}
                            inputValue={searchText}
                            onInputChange={(e, newVal) => setSearchText(newVal)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by Brand, Model, or Keywords..."
                                    variant="standard"
                                    onKeyDown={(e) => { 
                                        if (e.key === 'Enter') {
                                            // Prevent default form submission behavior if inside a form
                                            e.preventDefault();
                                            handleSearch(searchText); 
                                        }
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        disableUnderline: true,
                                        sx: { fontSize: { xs: '0.9rem', md: '1rem' }, color: '#444' }
                                    }}
                                />
                            )}
                            sx={{ flex: 1 }}
                        />

                        <Button
                            variant="contained"
                            onClick={() => handleSearch(searchText)}
                            sx={{
                                borderRadius: '50px',
                                minWidth: { xs: '80px', md: '120px' },
                                height: { xs: '40px', md: '45px' },
                                background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                boxShadow: 'none',
                                ml: 1,
                            }}
                        >
                            Search
                        </Button>
                    </Paper>
                </Grid>

                {/* --- 3. CAROUSEL --- */}
                <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                    <Interactive3DCarousel adSpaceList={adSpaceList} />
                </Grid>

            </Grid>
        </HeroWrapper>
    );
};

export default HeroSection;